import { useId, useState, type ReactNode } from "react";
import { copyText } from "@/lib/copy";

export interface GeneratorOutput {
  /** Stable key + analytics formatKey (e.g. the style id). */
  id: string;
  /** Short label shown above the result line. */
  label: string;
  /** The transformed, copyable text. */
  value: string;
}

interface Props {
  /** Computes the preview lines from the current input. Must be pure. */
  transform: (input: string) => GeneratorOutput[];
  placeholder?: string;
  /** Seed text so the page is useful before the visitor types. */
  defaultValue?: string;
  /** Optional controls (e.g. an intensity selector) rendered above the input. */
  controls?: ReactNode;
}

/**
 * Shared UI for every /tools generator: a text box, a live preview, and a
 * per-line copy button that routes through the central copy handler so the
 * Phase 20 "Popular" instrumentation sees generator copies too.
 */
export function TextGenerator({ transform, placeholder, defaultValue = "", controls }: Props) {
  const [input, setInput] = useState(defaultValue);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const inputId = useId();

  const outputs = transform(input);

  async function copy(out: GeneratorOutput) {
    const ok = await copyText(out.value, { source: "generator-line", formatKey: out.id });
    if (!ok) return;
    setCopiedId(out.id);
    window.setTimeout(() => setCopiedId((c) => (c === out.id ? null : c)), 1200);
  }

  return (
    <div className="mt-8">
      {controls ? <div className="mb-4">{controls}</div> : null}

      <label htmlFor={inputId} className="block text-sm font-medium text-foreground/90">
        Your text
      </label>
      <textarea
        id={inputId}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder={placeholder}
        rows={3}
        spellCheck={false}
        className="mt-2 w-full resize-y rounded-lg border border-border bg-surface/60 px-3 py-2.5 font-mono text-base text-foreground placeholder:text-muted-foreground focus-visible:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />

      <ul className="mt-6 space-y-2">
        {outputs.map((out) => {
          const isCopied = copiedId === out.id;
          const isEmpty = out.value.length === 0;
          return (
            <li key={out.id}>
              <button
                type="button"
                onClick={() => copy(out)}
                disabled={isEmpty}
                aria-label={`Copy ${out.label} text`}
                className="glass group flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default disabled:opacity-60"
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">
                    {out.label}
                  </span>
                  <span className="mt-1 block break-words text-lg leading-relaxed text-foreground">
                    {isEmpty ? <span className="text-muted-foreground">…</span> : out.value}
                  </span>
                </span>
                <span
                  className={`flex-none rounded border border-border bg-surface/60 px-2 py-1 text-[10px] uppercase tracking-wider ${
                    isCopied ? "text-accent" : "text-muted-foreground group-hover:text-foreground"
                  }`}
                  aria-hidden="true"
                >
                  {isCopied ? "Copied" : "Copy"}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
